import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Bell } from "lucide-react";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { employeeViewOf, notificationSettingsRoute, toViewPath } from "@/routes/employeePaths";
import { useMsalAuth } from "@/hooks/useMsalAuth";
import { useEmployeeChrome } from "@/hooks/useEmployeeChrome";
import { useEmployeeAccess } from "@/views/employee/access/useEmployeeAccess";
import {
  useGetEmployeeNotificationPrefsQuery,
  useGetEmployeeNotificationsQuery,
  useGetEmployeeUnreadCountQuery,
  useLazyGetEmployeeNotificationsQuery,
  useMarkAllEmployeeNotificationsReadMutation,
  useMarkEmployeeNotificationsReadMutation,
  useUpdateEmployeeNotificationPrefsMutation,
} from "@/store/api/employeeApi";
import { relativeTime } from "@/views/workforce/ui/relativeTime";
import { UNREAD_COUNT_CAP, type EmpNotification } from "../../../../shared/employee/notifications";
import { EmployeeAvatar } from "../ui/EmployeeAvatar";
import { notificationText, safeNotificationLink, withInstanceQuery } from "./notificationText";
import { usePageGuard } from "../ui/usePageGuard";

/** The live push can miss someone on another server; the count catches up. */
const COUNT_POLL_MS = 60_000;

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * The bell in the header, in skyEmployee only (decision P3-2) — its own pages
 * and a shared page opened from it, like Users & access — for anyone with a
 * directory record. The unread count comes from the database; a live push
 * refreshes it at once. It also records the browser's time zone once, so
 * quiet hours (on by default) fall in the person's own night.
 */
export function NotificationBell() {
  // Outside skyEmployee nothing is asked of skyEmployee at all.
  if (!useEmployeeChrome()) return null;
  return <EmployeeBell />;
}

function EmployeeBell() {
  const access = useEmployeeAccess();
  return access.me?.partyId ? <Bell_ /> : null;
}

function Bell_() {
  const [open, setOpen] = useState(false);
  const { data: count = 0 } = useGetEmployeeUnreadCountQuery(undefined, {
    pollingInterval: COUNT_POLL_MS,
  });
  useRecordTimeZone();

  const label =
    count === 0
      ? "Notifications"
      : `Notifications, ${count > UNREAD_COUNT_CAP ? `more than ${UNREAD_COUNT_CAP}` : count} unread`;

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className="relative flex h-9 w-9 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Bell
            className="h-5 w-5"
            aria-hidden="true"
          />
          {count > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 min-w-[18px] rounded-full bg-amber-500 px-1 text-center text-[11px] font-semibold leading-[18px] text-gray-900"
            >
              {count > UNREAD_COUNT_CAP ? `${UNREAD_COUNT_CAP}+` : count}
            </span>
          )}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          sideOffset={8}
          className="z-[1050] w-[min(380px,calc(100vw-24px))] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900"
        >
          {open && (
            <NotificationPanel
              unread={count}
              onClose={() => setOpen(false)}
            />
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Save the browser's time zone once, if it differs from what's stored. */
/**
 * Shared by every bell on the page (the header renders one for wide screens
 * and one for narrow), so the time zone is saved once, not once per bell.
 */
let timeZoneSaving = false;

function useRecordTimeZone() {
  const { data: prefs } = useGetEmployeeNotificationPrefsQuery();
  const [save] = useUpdateEmployeeNotificationPrefsMutation();
  const tried = useRef(false);
  useEffect(() => {
    if (!prefs || tried.current) return;
    tried.current = true;
    const tz = browserTimeZone();
    if (tz && tz !== prefs.timeZone && !timeZoneSaving) {
      timeZoneSaving = true;
      void Promise.resolve(save({ timeZone: tz }))
        .catch(() => {
          // Best effort: without it, quiet hours simply don't hold pushes.
        })
        .finally(() => {
          timeZoneSaving = false;
        });
    }
  }, [prefs, save]);
}

function NotificationPanel({ unread, onClose }: { unread: number; onClose: () => void }) {
  const navigate = useNavigate();
  // On a shared page (Users & access) links open in the admin view it's reached from.
  const pathname = employeeViewOf(useLocation().pathname);
  const { getQueryString } = useMsalAuth();
  const { data, isLoading, isError, refetch, fulfilledTimeStamp } =
    useGetEmployeeNotificationsQuery();
  const [loadOlder, older] = useLazyGetEmployeeNotificationsQuery();
  const [olderItems, setOlderItems] = useState<EmpNotification[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [olderError, setOlderError] = useState(false);
  // A fresh first page (a new notification pushed the list along) starts the
  // older ones over, so none is skipped or shown twice. One fetched again
  // only because we marked something read keeps them: reading doesn't move
  // anything, and they're marked here too.
  const refreshedByRead = useRef(false);
  useEffect(() => {
    if (refreshedByRead.current) {
      refreshedByRead.current = false;
      return;
    }
    setOlderItems((prev) => (prev.length === 0 ? prev : []));
    setCursor(null);
    setOlderError(false);
  }, [fulfilledTimeStamp]);
  const begin = usePageGuard(String(fulfilledTimeStamp ?? ""));
  const [markRead] = useMarkEmployeeNotificationsReadMutation();
  const [markAllRead, { isLoading: markingAll }] = useMarkAllEmployeeNotificationsReadMutation();

  const firstCursor = data?.nextCursor ?? null;
  const nextCursor = olderItems.length > 0 ? cursor : firstCursor;
  const items = [...(data?.items ?? []), ...olderItems];

  const showOlder = async () => {
    if (!nextCursor) return;
    setOlderError(false);
    const stillCurrent = begin();
    try {
      const page = await loadOlder({ before: nextCursor }).unwrap();
      if (!stillCurrent()) return;
      setOlderItems((prev) => [...prev, ...page.items]);
      setCursor(page.nextCursor);
    } catch {
      if (stillCurrent()) setOlderError(true);
    }
  };

  // Older items aren't in the cache the read marks update, so follow here.
  const markOlderRead = (ids: string[] | null) =>
    setOlderItems((prev) =>
      prev.map((n) => (ids === null || ids.includes(n.id) ? { ...n, read: true } : n)),
    );

  const open = (n: EmpNotification) => {
    if (!n.read) {
      markRead([n.id]).catch(() => {});
      markOlderRead([n.id]);
    }
    const link = safeNotificationLink(n.link);
    onClose();
    if (link) navigate(withInstanceQuery(toViewPath(link, pathname), getQueryString()));
  };

  return (
    <div className="flex max-h-[min(520px,70vh)] flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
        {unread > 0 && (
          <SkyButton
            appearance="subtle"
            size="small"
            onClick={() => {
              // The list refetched after this keeps the older ones (marked read).
              refreshedByRead.current = true;
              void markAllRead()
                .unwrap()
                .then(() => markOlderRead(null))
                .catch(() => {
                  refreshedByRead.current = false;
                });
            }}
            disabled={markingAll}
          >
            Mark all read
          </SkyButton>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <p className="px-4 py-6 text-center text-sm text-gray-500">Loading…</p>
        ) : isError ? (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">Notifications couldn't load.</p>
            <SkyButton
              appearance="secondary"
              size="small"
              onClick={() => refetch()}
            >
              Try again
            </SkyButton>
          </div>
        ) : items.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            You're all caught up.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => open(n)}
                  className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none dark:hover:bg-gray-800 dark:focus-visible:bg-gray-800 ${
                    n.read ? "" : "bg-amber-50/60 dark:bg-amber-950/20"
                  }`}
                >
                  {n.actor ? (
                    <EmployeeAvatar
                      name={n.actor.name}
                      photoUrl={n.actor.photoUrl}
                      size={32}
                    />
                  ) : (
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                      <Bell
                        className="h-4 w-4 text-gray-500"
                        aria-hidden="true"
                      />
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-gray-900 dark:text-gray-100">
                      {notificationText(n)}
                    </span>
                    {n.snippet && (
                      <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400">
                        {n.snippet}
                      </span>
                    )}
                    <span className="mt-0.5 block text-xs text-gray-400">
                      {relativeTime(n.createdAt)}
                    </span>
                  </span>
                  {!n.read && (
                    <span
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-500"
                      aria-label="Unread"
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
        {nextCursor && !isError && (
          <div className="px-4 py-2 text-center">
            <SkyButton
              appearance="subtle"
              size="small"
              onClick={showOlder}
              disabled={older.isFetching}
            >
              {older.isFetching ? "Loading…" : olderError ? "Try again" : "Show older"}
            </SkyButton>
            {olderError && (
              <p
                role="alert"
                className="mt-1 text-xs text-gray-600 dark:text-gray-300"
              >
                Older notifications couldn't load.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 px-4 py-2 dark:border-gray-700">
        <SkyButton
          appearance="transparent"
          size="small"
          onClick={() => {
            onClose();
            // Stay in the view (admin or employee) the bell was opened from.
            navigate(notificationSettingsRoute(pathname) + getQueryString());
          }}
        >
          Notification settings
        </SkyButton>
      </div>
    </div>
  );
}
